import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Box, Divider, FormControlLabel, IconButton, Stack, Switch, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import HelpIcon from '@mui/icons-material/Help';
import Skeleton from '@mui/material/Skeleton';
import { CommonSettingCardProps } from './CommonSettingCard.props';
import './CommonSettingCard.styles.css';
import { Toast } from '../../atoms/Toast';
import { LoadingPage } from '../../organisms/LoadingPage';
import { Modal } from '../../atoms/Modal';

const GreenSwitch = styled(Switch)(() => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#008545',
    '&:hover': {
      backgroundColor: '#B3DAC7',
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#B3DAC7',
  },
}));

export const CommonSettingCard: React.FC<CommonSettingCardProps> = ({
  title,
  titleFlexDirection = 'column',
  description,
  helperText,
  status,
  loading,
  tooltipContent,
  onToggle,
  successMessage,
  errorMessage,
  showDisableDialog = false,
  showEnableDialog = false,
  disableDialogTitle = '',
  disableDialogContent = <></>,
  onDisabled = () => {},
  onEnableToggle = () => {},
}) => {
  const [checked, setChecked] = useState<boolean>(status ?? false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [toastValue, setToastValue] = useState({ type: '', message: '', isOpen: false, toastId: 0 });
  const [modalID, setModalID] = useState(1);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [isOpenOffDialog, setIsOpenOffDialog] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    setChecked(status ?? false);
  }, [status]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleToggle = async (newValue: boolean) => {
    setLoadingPage(true);
    try {
      const response = await onToggle(newValue);
      const isSuccess = response?.status === 200 && response?.data !== 'False to update';
      if (!isMountedRef.current) {
        return;
      }
      if (isSuccess) {
        setChecked(newValue);
        setModalID(modalID + 1);
        setToastValue({
          type: 'success',
          message: successMessage ?? 'Update setting successfully!',
          isOpen: true,
          toastId: modalID,
        });
        if (newValue) {
          onEnableToggle();
        } else {
          onDisabled();
        }
      } else {
        setModalID(modalID + 1);
        setToastValue({
          type: 'error',
          message: errorMessage ?? 'Something went wrong!',
          isOpen: true,
          toastId: modalID,
        });
      }
    } catch (e) {
      if (!isMountedRef.current) {
        return;
      }
      setModalID(modalID + 1);
      setToastValue({
        type: 'error',
        message: errorMessage ?? 'Something went wrong!',
        isOpen: true,
        toastId: modalID,
      });
    } finally {
      if (isMountedRef.current) {
        setLoadingPage(false);
      }
    }
  };

  const onValueOff = () => {
    setIsOpenOffDialog(false);
    handleToggle(false);
  };

  const onValueOn = () => {
    setIsOpenDialog(false);
    handleToggle(true);
  };

  const onStatusChange = (newValue: boolean) => {
    if (newValue === false && showDisableDialog) {
      setIsOpenOffDialog(true);
    } else if (newValue === true && showEnableDialog) {
      setIsOpenDialog(true);
    } else {
      handleToggle(newValue);
    }
  };

  const renderTitle = (titleValue?: string | ReactElement) => {
    if (typeof titleValue === 'string') {
      return (
        <Box
          className="common-setting-card-title"
          sx={{
            fontFamily: 'Montserrat',
            fontStyle: 'normal',
            fontWeight: '700',
            fontSize: '16px',
            color: '#212121',
          }}
        >
          {titleValue}
        </Box>
      );
    }
    return titleValue;
  };

  return (
    <Box
      className="common-setting-wrapper"
      sx={{
        background: '#fff',
        border: '1px solid #E0E0E0',
        borderRadius: '4px',
        minHeight: '80px',
        boxSizing: 'border-box',
      }}
    >
      <Box className="common-setting-content" sx={{ margin: '16px' }}>
        <Stack direction={titleFlexDirection}>
          {title && (loading ? <Skeleton animation="wave" sx={{ width: '200px', height: '20px' }} /> : renderTitle(title))}
          {loading ? (
            <Skeleton animation="wave" sx={{ width: '350px', height: '40px' }} />
          ) : (
            <Box
              className="common-setting-content-detail"
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: '-8px',
                fontFamily: 'Montserrat',
                fontStyle: 'normal',
                fontWeight: '600',
                fontSize: '14px',
              }}
            >
              <FormControlLabel
                className={`status-setting ${checked ? 'enabled' : 'disabled'}`}
                control={
                  <GreenSwitch
                    checked={checked}
                    disabled={loadingPage}
                    onChange={(event) => onStatusChange(event.target.checked)}
                  />
                }
                label={description}
              />
              {tooltipContent ? (
                <Tooltip placement="top" title={tooltipContent}>
                  <IconButton className="info-action">
                    <HelpIcon fontSize="small" htmlColor="#193051" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
          )}
        </Stack>
        <Divider style={{ marginTop: 8, marginBottom: 16 }}/>
        {helperText && (
          <Box sx={{ fontSize: '14px', fontWeight:'400px'}}>{helperText}</Box>
        )}
      </Box>
      <Toast
        isOpen={toastValue.isOpen}
        type={toastValue.type}
        message={toastValue.message}
        toastId={toastValue.toastId}
        autoHideDuration={2000}
      />
      <LoadingPage open={loadingPage} />
      <Modal
        idModal="modal-common-setting-card-on-click"
        openDialog={isOpenDialog}
        variant="delete"
        title="Enable setting"
        actionAgree={() => onValueOn()}
        actionCancel={() => setIsOpenDialog(false)}
        textAgree="Enable"
        onClose={() => setIsOpenDialog(false)}
        children={disableDialogContent ? disableDialogContent : <></>}
      />
      <Modal
        idModal="modal-common-setting-card-off-click"
        openDialog={isOpenOffDialog}
        variant="delete"
        title={disableDialogTitle || 'Title'}
        actionAgree={() => onValueOff()}
        actionCancel={() => setIsOpenOffDialog(false)}
        textAgree="Disable"
        onClose={() => setIsOpenOffDialog(false)}
        children={disableDialogContent ? disableDialogContent : <></>}
      />
    </Box>
  );
};
